import {Component, ElementRef, EventEmitter, forwardRef, Input, OnInit, Output, ViewChild, ViewEncapsulation,} from '@angular/core';
import {VariableValue} from './variable-value.model';
import {PredictionModelDto, VariableValueDto} from '@sharedcommon/rest/data.restservice';
import {VariableDto} from '@sharedcommon/rest/equation.restservice';
import {ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR, Validators} from '@angular/forms';
import {filter, takeUntil} from 'rxjs/operators';
import {Constants} from '@sharedcommon/util/constants.util';
import {TranslateService} from '@ngx-translate/core';
import {BehaviorSubject, Subject} from 'rxjs';
import {Rights} from '@sharedcommon/util/rights.util';
import {UserService} from '@sharedcommon/services/user.service';
import {MatTooltip} from '@angular/material/tooltip';
import {MatAutocompleteTrigger} from '@angular/material/autocomplete';
import { FunctionUtils } from '@sharedcommon/util/functions.util';

@Component({
  selector: 'neq-variable-value',
  templateUrl: './variable-value.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NeqVariableValueComponent),
      multi: true
    }
  ],
  encapsulation: ViewEncapsulation.None,
})
export class NeqVariableValueComponent implements OnInit, ControlValueAccessor {

  @Output() update = new EventEmitter<VariableValue>();
  @Output() change = new EventEmitter();
  @Output() blur = new EventEmitter();

  @Input() predictionModel: PredictionModelDto;
  @Input() variable: VariableDto;
  @Input() filter = true;

  /**
   * Number of dynamic column present
   */
  @Input() numberDynamicColumn = 0;

  @ViewChild('tooltip') tooltip: MatTooltip;
  @ViewChild('input') input: ElementRef;
  @ViewChild('trigger') matAutocompleteTrigger: MatAutocompleteTrigger;

  // FormControl functions
  private valueAccessorOnChange: (_: any) => {};
  private valueAccessorOnTouched: () => {};
  private readonly updateFormSubject = new BehaviorSubject(null);
  private lastValue: string = null;

  public editVarVal = [Rights.A_VAR_VAL_UPDATE];

  variableValueForm: FormGroup;

  private readonly destroy$ = new Subject<any>();

  constructor(private readonly translate: TranslateService, private readonly userService: UserService) {
  }

  ngOnInit() {
    this.variableValueForm = new FormGroup({
      variableValue: new FormControl(null)
    });

    if (this.variable.conditionVariable) {
      this.variableValueForm.get('variableValue').setValidators([
        Validators.pattern(Constants.REGEX_ALPHA_NUM)]);
      this.variableValueForm.get('variableValue').valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(value => {
          this.updateVariable(value);
        });
    } else {
      this.variableValueForm.get('variableValue').setValidators([
        Validators.pattern(Constants.REGEX_DECIMAL),
        Validators.min(Number(this.variable.min)),
        Validators.max(Number(this.variable.max))]);
    }

    this.variableValueForm.valueChanges.subscribe(_ => {
      this.manageFieldError();
    });

    this.updateFormSubject.pipe(filter(x => x !== null)).subscribe(_ => {
      const value = this.variableValueForm.get('variableValue').value;
      this.updateVariable(value);
    });

    if (!this.userService.hasRights(this.editVarVal)) {
      this.variableValueForm.disable();
    }
  }

  onChange(event: any) {
    this.change.emit(event);
    if (this.valueAccessorOnChange) {
      this.valueAccessorOnChange(event);
    }
  }

  updateVariable(value: string) {
    const isCondition = this.variable.conditionVariable;
    if (
      this.variableValueForm.valid
      && value !== this.lastValue
      && (
        /* Check if valid value or Not in condition vaiable or if the entered value is an authorized one */
        FunctionUtils.isNotDefinedOrEmptyString(value) || !isCondition || this.variable.possibleValues.includes(value)
      )
    ) {
      this.lastValue = value;
      const newVariableValue = new VariableValueDto();
      newVariableValue.value = value;
      this.update.emit(new VariableValue(this.predictionModel, this.variable, newVariableValue));
    }
  }

  onBlur(event: any) {
    this.blur.emit(event);
    if (this.valueAccessorOnTouched) {
      this.valueAccessorOnTouched();
    }
  }

  writeValue(value: any): void {
    this.variableValueForm.setValue({'variableValue': value}, {emitEvent: false});
  }

  registerOnChange(fn: any): void {
    this.valueAccessorOnChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.valueAccessorOnTouched = fn;
  }

  getTooltipErrorMessage() {
    const errors = this.variableValueForm.get('variableValue').errors;
    let message = '';
    if (errors) {
      if (errors.pattern) {
        if (this.variable.conditionVariable) {
          message = this.translate.instant('error.common.alphanumeric.invalid');
        } else {
          message = this.translate.instant('error.common.decimal.invalid');
        }
      } else if (errors.min) {
        message = this.translate.instant('error.common.numeric.min', {param: this.variable.min});
      } else if (errors.max) {
        message = this.translate.instant('error.common.numeric.max', {param: this.variable.max});
      }
    }

    return message;
  }

  filterPossibleValues() {
    if (this.filter) {
      const input = this.variableValueForm.get('variableValue').value;
      return this.variable.possibleValues.filter(value => value.indexOf(input) !== -1 || input === null);
    } else {
      return this.variable.possibleValues;
    }
  }

  onUpdate(event: any) {
    event.stopPropagation();
    this.updateFormSubject.next(event);
  }

  /**
   * After the user as use enter key the value is save and the next input in the list is focus
   *
   * @param event
   */
  enterUpdate(event: any): void {
    event.stopPropagation();
    const element: HTMLCollection = document.getElementsByTagName('input');
    const elementArray = [].slice.call(element);
    const index = elementArray.indexOf(event.target);
    this.updateFormSubject.next(event);
    const nextInput: HTMLInputElement = elementArray[index + this.numberDynamicColumn];
    nextInput.click();
  }

  private manageFieldError() {
    if (this.variableValueForm.get('variableValue').invalid) {
      this.tooltip.show();
    } else {
      this.tooltip.hide();
    }
  }

  displayAutocomplete(): boolean {
    const variableValue = this.variableValueForm.get('variableValue').value;

    return this.variable.conditionVariable && !variableValue;
  }

  focus() {
    if (this.matAutocompleteTrigger) {
      this.matAutocompleteTrigger.closePanel();
    }
    this.input.nativeElement.focus();
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

}
